import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Search, 
  Calendar, 
  Users, 
  FileText, 
  Download, 
  ExternalLink, 
  ChevronDown, 
  ChevronUp, 
  GraduationCap, 
  Award,
  Star,
  Flame,
  Copy,
  Check,
  Globe,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { getFileUrl } from '../lib/utils';

const PublishedResearch = () => {
  const { theme } = useTheme();
  const [researchList, setResearchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedJournal, setSelectedJournal] = useState('all');
  const [selectedQuartile, setSelectedQuartile] = useState('all');
  const [onlyHighImpact, setOnlyHighImpact] = useState(false);
  const [expandedAbstracts, setExpandedAbstracts] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchResearch();
  }, []);

  const fetchResearch = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/research/`);
      const data = await response.json();
      setResearchList(data);
    } catch (error) {
      console.error('Error fetching research:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAbstract = (id) => {
    setExpandedAbstracts(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleCopyCitation = (id, citation, title) => {
    const textToCopy = citation || title;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2500);
      });
    }
  };

  // Extract filter options
  const years = useMemo(() => {
    const set = new Set();
    researchList.forEach(r => {
      if (r.publication_date) {
        const yearMatch = r.publication_date.match(/\b(19\d\d|20\d\d)\b/);
        if (yearMatch) set.add(yearMatch[1]);
      }
    });
    return Array.from(set).sort().reverse();
  }, [researchList]);

  const journals = useMemo(() => {
    const set = new Set();
    researchList.forEach(r => {
      if (r.journal_name && r.journal_name.trim()) {
        set.add(r.journal_name.trim());
      }
    });
    return Array.from(set).sort();
  }, [researchList]);

  const stats = useMemo(() => {
    const total = researchList.length;
    const q1Count = researchList.filter(r => r.quartile === 'Q1').length;
    const highIfCount = researchList.filter(r => {
      const ifVal = parseFloat(r.impact_factor);
      return !isNaN(ifVal) && ifVal >= 5.0;
    }).length;
    return { total, q1Count, highIfCount };
  }, [researchList]);

  // Filtered research list
  const filteredResearch = useMemo(() => {
    return researchList.filter(item => {
      const q = searchTerm.toLowerCase().trim();
      const matchSearch = !q || (
        (item.title_ar && item.title_ar.toLowerCase().includes(q)) ||
        (item.title_en && item.title_en.toLowerCase().includes(q)) ||
        (Array.isArray(item.authors) && item.authors.some(a => a.toLowerCase().includes(q))) ||
        (item.journal_name && item.journal_name.toLowerCase().includes(q)) ||
        (item.indexing && item.indexing.toLowerCase().includes(q)) ||
        (item.citation && item.citation.toLowerCase().includes(q)) ||
        (item.abstract_ar && item.abstract_ar.toLowerCase().includes(q)) ||
        (item.doi && item.doi.toLowerCase().includes(q))
      );

      const yearMatch = item.publication_date ? item.publication_date.match(/\b(19\d\d|20\d\d)\b/) : null;
      const itemYear = yearMatch ? yearMatch[1] : '';
      const matchYear = selectedYear === 'all' || itemYear === selectedYear;

      const matchJournal = selectedJournal === 'all' || 
        (item.journal_name && item.journal_name.trim() === selectedJournal);

      const matchQuartile = selectedQuartile === 'all' || item.quartile === selectedQuartile;

      const ifVal = parseFloat(item.impact_factor);
      const matchHighImpact = !onlyHighImpact || (!isNaN(ifVal) && ifVal >= 5.0);

      return matchSearch && matchYear && matchJournal && matchQuartile && matchHighImpact;
    });
  }, [researchList, searchTerm, selectedYear, selectedJournal, selectedQuartile, onlyHighImpact]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredResearch.length / itemsPerPage);
  const displayedResearch = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredResearch.slice(start, start + itemsPerPage);
  }, [filteredResearch, currentPage]);

  const getPdfUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedYear('all');
    setSelectedJournal('all');
    setSelectedQuartile('all');
    setOnlyHighImpact(false);
    setCurrentPage(1);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      theme === 'dark' ? 'bg-[#060e1a] text-gray-100' : 'bg-[#f8fafc] text-gray-900'
    }`}>
      {/* 
        HERO SECTION 
        Note: paddingTop 200px ensures ample clearance under the fixed 80px navbar
      */}
      <section 
        className="research-hero-section relative text-white overflow-hidden shadow-lg"
        style={{
          backgroundColor: '#071738',
          backgroundImage: 'linear-gradient(180deg, #071738 0%, #0A225C 35%, #0F388E 70%, #123E9C 100%)',
          paddingTop: '200px',
          paddingBottom: '70px',
          color: '#ffffff'
        }}
      >
        {/* Subtle Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(#C9A040_1px,transparent_1px)] [background-size:24px_24px]"></div>
        </div>

        {/* Ambient Glows */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#C9A040]/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          {/* Top Subtitle Badge */}
          <div 
            className="research-top-badge inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6 text-sm font-bold shadow-md"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: '#FFD54F'
            }}
          >
            <GraduationCap className="w-4 h-4 text-[#FFD54F]" />
            <span>جامعة ابن النفيس للعلوم الطبية — مركز البحث العلمي والنشر</span>
          </div>

          {/* Main Title */}
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mb-4"
            style={{ color: '#ffffff', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
          >
            دليل الأبحاث العلمية والإنتاج البحثي
          </h1>

          {/* Golden Divider */}
          <div style={{ width: '100px', height: '5px', backgroundColor: '#C9A040', borderRadius: '9999px', margin: '0 auto 24px auto' }}></div>

          {/* Subtitle */}
          <p 
            className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-12 font-medium"
            style={{ color: '#e0e7ff' }}
          >
            سجل الإنتاج العلمي المحكم لباحثي وكوادر الجامعة، المنشور في أرقى الدوريات الطبية العالمية المفهرسة في <span style={{ color: '#FFD54F', fontWeight: 700 }}>Web of Science</span> و <span style={{ color: '#FFD54F', fontWeight: 700 }}>Scopus</span> و <span style={{ color: '#FFD54F', fontWeight: 700 }}>PubMed</span> بما في ذلك دوريات <span style={{ color: '#FFD54F', fontWeight: 700 }}>The Lancet</span> و <span style={{ color: '#FFD54F', fontWeight: 700 }}>Nature</span>.
          </p>

          {/* High-Contrast Quick Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {/* Total Research */}
            <div 
              className="research-stat-card transition-transform hover:scale-105"
              style={{
                backgroundColor: 'rgba(6, 20, 48, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
              }}
            >
              <div className="research-stat-num-gold" style={{ fontSize: '2.5rem', fontWeight: 900, color: '#FFD54F', lineHeight: 1 }}>
                {stats.total}
              </div>
              <div className="research-stat-title" style={{ color: '#ffffff', fontWeight: 700, fontSize: '0.95rem', marginTop: '8px' }}>
                بحث علمي ودراسة
              </div>
              <div className="research-stat-subtitle" style={{ color: '#93c5fd', fontSize: '0.75rem', marginTop: '4px' }}>
                إنتاج بحثي معتمد
              </div>
            </div>

            {/* Q1 Count */}
            <div 
              className="research-stat-card transition-transform hover:scale-105"
              style={{
                backgroundColor: 'rgba(6, 20, 48, 0.9)',
                border: '1px solid rgba(52, 211, 153, 0.4)',
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
              }}
            >
              <div className="research-stat-num-emerald" style={{ fontSize: '2.5rem', fontWeight: 900, color: '#34d399', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Star className="w-6 h-6 fill-emerald-400 text-emerald-400" />
                <span>{stats.q1Count}</span>
              </div>
              <div className="research-stat-title" style={{ color: '#a7f3d0', fontWeight: 700, fontSize: '0.95rem', marginTop: '8px' }}>
                بحث مصنف Q1 عالمياً
              </div>
              <div className="research-stat-subtitle" style={{ color: '#6ee7b7', fontSize: '0.75rem', marginTop: '4px' }}>
                الربع الأول الأعلى تأثيراً
              </div>
            </div>

            {/* High Impact */}
            <div 
              className="research-stat-card transition-transform hover:scale-105"
              style={{
                backgroundColor: 'rgba(6, 20, 48, 0.9)',
                border: '1px solid rgba(251, 113, 133, 0.4)',
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
              }}
            >
              <div className="research-stat-num-rose" style={{ fontSize: '2.5rem', fontWeight: 900, color: '#fb7185', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Flame className="w-6 h-6 fill-rose-400 text-rose-400" />
                <span>{stats.highIfCount}</span>
              </div>
              <div className="research-stat-title" style={{ color: '#fecdd3', fontWeight: 700, fontSize: '0.95rem', marginTop: '8px' }}>
                أبحاث فائقة التأثير
              </div>
              <div className="research-stat-subtitle" style={{ color: '#fda4af', fontSize: '0.75rem', marginTop: '4px' }}>
                معامل تأثير IF ≥ 5.0
              </div>
            </div>

            {/* Max Impact Factor */}
            <div 
              className="research-stat-card transition-transform hover:scale-105"
              style={{
                backgroundColor: 'rgba(6, 20, 48, 0.9)',
                border: '1px solid rgba(255, 213, 79, 0.4)',
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
              }}
            >
              <div className="research-stat-num-gold" style={{ fontSize: '2.5rem', fontWeight: 900, color: '#FFD54F', lineHeight: 1 }}>
                88.5
              </div>
              <div className="research-stat-title" style={{ color: '#fde68a', fontWeight: 700, fontSize: '0.95rem', marginTop: '8px' }}>
                أعلى معامل تأثير (IF)
              </div>
              <div className="research-stat-subtitle" style={{ color: '#fcd34d', fontSize: '0.75rem', marginTop: '4px' }}>
                The Lancet Journal
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT AREA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-8 bg-white dark:bg-[#0c1b30] px-4 py-2.5 rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm w-fit">
          <Link to="/" className="hover:text-[#0F388E] dark:hover:text-[#64B5F6] transition-colors font-medium">الرئيسية</Link>
          <span>/</span>
          <Link to="/departments/research-center" className="hover:text-[#0F388E] dark:hover:text-[#64B5F6] transition-colors font-medium">مركز البحث العلمي</Link>
          <span>/</span>
          <span className="text-[#0F388E] dark:text-[#90CAF9] font-bold">الأبحاث المنشورة</span>
        </div>

        {/* SEARCH & FILTER CONTROLS */}
        <div className={`p-6 rounded-2xl mb-10 shadow-sm border transition-colors ${
          theme === 'dark' 
            ? 'bg-[#0B1A2F] border-gray-800' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Quick Filter Buttons (Pills) */}
          <div className="flex items-center gap-2.5 overflow-x-auto pb-4 mb-5 border-b border-gray-100 dark:border-gray-800 scrollbar-thin">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
              تصنيفات سريعة:
            </span>
            
            <button
              onClick={() => { setSelectedQuartile('all'); setOnlyHighImpact(false); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                selectedQuartile === 'all' && !onlyHighImpact
                  ? 'bg-[#0F388E] text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              جميع الأبحاث ({researchList.length})
            </button>

            <button
              onClick={() => { setSelectedQuartile('Q1'); setOnlyHighImpact(false); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all whitespace-nowrap ${
                selectedQuartile === 'Q1'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100'
              }`}
            >
              <Star className="w-3.5 h-3.5 fill-current" />
              أبحاث Q1 الأعلى تصنيفاً ({stats.q1Count})
            </button>

            <button
              onClick={() => { setOnlyHighImpact(prev => !prev); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all whitespace-nowrap ${
                onlyHighImpact
                  ? 'bg-rose-600 text-white shadow-md'
                  : 'bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border border-rose-200 dark:border-rose-800 hover:bg-rose-100'
              }`}
            >
              <Flame className="w-3.5 h-3.5 fill-current" />
              أبحاث التأثير العالي (IF ≥ 5) ({stats.highIfCount})
            </button>

            <button
              onClick={() => { setSelectedQuartile('Q2'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                selectedQuartile === 'Q2'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100'
              }`}
            >
              تصنيف Q2
            </button>

            <button
              onClick={() => { setSelectedQuartile('Q3'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                selectedQuartile === 'Q3'
                  ? 'bg-amber-600 text-white shadow-md'
                  : 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100'
              }`}
            >
              تصنيف Q3
            </button>
          </div>

          {/* Search and Dropdowns Row */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            {/* Search Input */}
            <div className="md:col-span-6 relative">
              <Search className="w-5 h-5 absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ابحث بالعنوان، اسم الباحث، المجلة، DOI، أو قواعد الفهرسة (Scopus / Web of Science)..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className={`w-full pr-11 pl-12 py-3 rounded-xl border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0F388E] transition-all ${
                  theme === 'dark'
                    ? 'bg-[#152843] border-gray-700 text-white placeholder-gray-400'
                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                }`}
              />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded-md"
                >
                  مسح
                </button>
              )}
            </div>

            {/* Filter by Year */}
            <div className="md:col-span-3">
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setCurrentPage(1);
                }}
                className={`w-full px-4 py-3 rounded-xl border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0F388E] transition-all cursor-pointer ${
                  theme === 'dark'
                    ? 'bg-[#152843] border-gray-700 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                }`}
              >
                <option value="all">جميع سنوات النشر ({years.length})</option>
                {years.map(y => (
                  <option key={y} value={y}>سنة {y}</option>
                ))}
              </select>
            </div>

            {/* Filter by Journal */}
            <div className="md:col-span-3">
              <select
                value={selectedJournal}
                onChange={(e) => {
                  setSelectedJournal(e.target.value);
                  setCurrentPage(1);
                }}
                className={`w-full px-4 py-3 rounded-xl border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0F388E] transition-all cursor-pointer ${
                  theme === 'dark'
                    ? 'bg-[#152843] border-gray-700 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                }`}
              >
                <option value="all">جميع المجلات والدوريات ({journals.length})</option>
                {journals.map(j => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filter Summary */}
          {(searchTerm || selectedYear !== 'all' || selectedJournal !== 'all' || selectedQuartile !== 'all' || onlyHighImpact) && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 font-bold">النتائج المطابقة:</span>
                <span className="text-[#0F388E] dark:text-[#64B5F6] font-extrabold text-sm">
                  {filteredResearch.length} بحث
                </span>
              </div>
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 text-rose-600 hover:text-rose-700 dark:text-rose-400 font-bold transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>إعادة ضبط المرشحات</span>
              </button>
            </div>
          )}
        </div>

        {/* RESEARCH LIST */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="animate-spin rounded-full h-14 w-14 border-4 border-[#0F388E] border-t-transparent mb-4"></div>
            <p className="text-gray-500 font-medium">جاري تحميل الأبحاث العلمية...</p>
          </div>
        ) : filteredResearch.length === 0 ? (
          <div className={`text-center py-20 rounded-2xl border ${
            theme === 'dark' ? 'bg-[#0B1A2F] border-gray-800' : 'bg-white border-gray-200'
          }`}>
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">
              لم يتم العثور على أبحاث مطابقة
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              يرجى تجربة كلمات بحث أخرى أو إزالة قيود التصفية لعرض جميع الأبحاث
            </p>
            <Button
              onClick={resetFilters}
              className="bg-[#0F388E] hover:bg-[#0F388E]/90 text-white font-bold"
            >
              عرض جميع الأبحاث
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {displayedResearch.map((item, index) => {
              const isExpanded = !!expandedAbstracts[item.id];
              const yearMatch = item.publication_date ? item.publication_date.match(/\b(19\d\d|20\d\d)\b/) : null;
              const year = yearMatch ? yearMatch[1] : '';
              const pdfUrl = item.pdf_url ? getFileUrl(item.pdf_url) : '';
              const isCopied = copiedId === item.id;
              const hasHighIf = parseFloat(item.impact_factor) >= 5.0;

              return (
                <article
                  key={item.id}
                  className={`rounded-2xl border transition-all duration-300 hover:shadow-xl group overflow-hidden ${
                    theme === 'dark'
                      ? 'bg-[#0B1A2F] border-gray-800/90 hover:border-[#C9A040]/60'
                      : 'bg-white border-gray-200 hover:border-[#0F388E]/40'
                  }`}
                >
                  <div className="p-6 sm:p-7">
                    {/* Top Row: Meta Badges */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      {/* Left: Academic Badges */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Paper Number */}
                        <span className="w-8 h-8 rounded-lg bg-[#0F388E] text-white text-xs font-black flex items-center justify-center shadow-sm">
                          #{item.doc_number || ((currentPage - 1) * itemsPerPage + index + 1)}
                        </span>

                        {/* Quartile Badge */}
                        {item.quartile && (
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black shadow-sm ${
                            item.quartile.toUpperCase().includes('Q1')
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                              : item.quartile.toUpperCase().includes('Q2')
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border border-blue-300 dark:border-blue-800'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-300 dark:border-amber-800'
                          }`}>
                            {item.quartile.toUpperCase().includes('Q1') && (
                              <Star className="w-3.5 h-3.5 fill-emerald-500 text-emerald-600" />
                            )}
                            <span>تصنيف {item.quartile}</span>
                          </span>
                        )}

                        {/* Impact Factor Badge */}
                        {item.impact_factor && (
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black shadow-sm ${
                            hasHighIf
                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/70 dark:text-rose-300 border border-rose-300 dark:border-rose-800'
                              : 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300 border border-orange-200'
                          }`}>
                            {hasHighIf && <Flame className="w-3.5 h-3.5 text-rose-600 fill-rose-500" />}
                            <span>IF: {item.impact_factor}</span>
                          </span>
                        )}

                        {/* Year Badge */}
                        {year && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                            <Calendar className="w-3 h-3 text-slate-500" />
                            <span>{year}</span>
                          </span>
                        )}
                      </div>

                      {/* Right: Publication Status / Date */}
                      <div className="flex items-center gap-2">
                        {item.publication_status && (
                          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-md ${
                            item.publication_status.toLowerCase().includes('under') || item.publication_status.toLowerCase().includes('review')
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200'
                              : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200'
                          }`}>
                            {item.publication_status}
                          </span>
                        )}
                        {item.publication_date && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                            {item.publication_date}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Paper Title (LTR for proper English display and typography) */}
                    <h2 
                      dir="ltr" 
                      className="text-left text-lg sm:text-xl font-bold leading-relaxed mb-3 text-gray-900 dark:text-white group-hover:text-[#0F388E] dark:group-hover:text-[#64B5F6] transition-colors"
                      style={{ color: theme === 'dark' ? '#f8fafc' : '#0f172a', textAlign: 'left' }}
                    >
                      {item.title_en || item.title_ar}
                    </h2>

                    {/* Journal & Indexing Row */}
                    <div className="flex flex-wrap items-center gap-2.5 mb-4">
                      {/* Journal Name */}
                      {item.journal_name && (
                        <div 
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold"
                          style={{
                            backgroundColor: theme === 'dark' ? 'rgba(120, 53, 15, 0.4)' : '#fef3c7',
                            color: theme === 'dark' ? '#fde68a' : '#854d0e',
                            border: '1px solid ' + (theme === 'dark' ? 'rgba(180, 83, 9, 0.5)' : '#fde68a')
                          }}
                        >
                          <BookOpen className="w-3.5 h-3.5 text-[#C9A040]" />
                          <span>{item.journal_name}</span>
                        </div>
                      )}

                      {/* Indexing Badges */}
                      {item.indexing && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {item.indexing.split(/;|,/).map((idxStr, i) => {
                            const clean = idxStr.trim();
                            if (!clean || clean === 'N/A') return null;
                            return (
                              <span 
                                key={i}
                                className="text-[11px] font-semibold px-2 py-0.5 rounded-md"
                                style={{
                                  backgroundColor: theme === 'dark' ? 'rgba(12, 74, 110, 0.5)' : '#e0f2fe',
                                  color: theme === 'dark' ? '#7dd3fc' : '#0369a1',
                                  border: '1px solid ' + (theme === 'dark' ? 'rgba(2, 132, 199, 0.4)' : '#bae6fd')
                                }}
                              >
                                {clean}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Authors List (LTR) */}
                    {Array.isArray(item.authors) && item.authors.length > 0 && (
                      <div className="mb-4 text-xs sm:text-sm">
                        <div className="flex items-start gap-2">
                          <Users className="w-4 h-4 text-[#C9A040] flex-shrink-0 mt-0.5" />
                          <div dir="ltr" className="text-left flex-1 font-medium leading-relaxed">
                            <span 
                              className="font-bold mr-1.5 font-sans"
                              style={{ color: theme === 'dark' ? '#94a3b8' : '#475569' }}
                            >
                              Authors:
                            </span>
                            <span style={{ color: theme === 'dark' ? '#f1f5f9' : '#1e293b', fontWeight: 600 }}>
                              {item.authors.join(' • ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Chicago Citation Box */}
                    {item.citation && (
                      <div 
                        className="mb-4 p-3.5 rounded-xl flex items-start justify-between gap-3"
                        style={{
                          backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc',
                          border: '1px solid ' + (theme === 'dark' ? '#1e293b' : '#e2e8f0')
                        }}
                      >
                        <div dir="ltr" className="text-left text-xs leading-relaxed flex-1 font-mono">
                          <span 
                            className="font-bold block mb-1 font-sans text-xs"
                            style={{ color: theme === 'dark' ? '#90CAF9' : '#0F388E' }}
                          >
                            Chicago Citation:
                          </span>
                          <span style={{ color: theme === 'dark' ? '#cbd5e1' : '#1e293b', fontWeight: 500 }}>
                            {item.citation}
                          </span>
                        </div>
                        <button
                          onClick={() => handleCopyCitation(item.id, item.citation, item.title_en)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-white dark:bg-[#1A3152] border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:text-[#0F388E] dark:hover:text-[#64B5F6] shadow-sm transition-all flex-shrink-0"
                          title="نسخ التوثيق"
                        >
                          {isCopied ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-emerald-600">تم النسخ!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              <span>نسخ التوثيق</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Abstract (if present) */}
                    {item.abstract_ar && (
                      <div className="mb-5 p-4 rounded-xl bg-gray-50/70 dark:bg-[#12243d] border border-gray-100 dark:border-gray-800">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-bold text-[#0F388E] dark:text-[#90CAF9] flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            ملخص البحث (Abstract)
                          </span>
                          <button
                            onClick={() => toggleAbstract(item.id)}
                            className="text-xs text-[#C9A040] hover:text-[#9E7818] dark:hover:text-[#FFD54F] font-bold inline-flex items-center gap-1 transition-colors"
                          >
                            {isExpanded ? (
                              <>
                                <span>إخفاء الملخص</span>
                                <ChevronUp className="w-3.5 h-3.5" />
                              </>
                            ) : (
                              <>
                                <span>عرض الملخص</span>
                                <ChevronDown className="w-3.5 h-3.5" />
                              </>
                            )}
                          </button>
                        </div>
                        <p className={`text-sm text-gray-600 dark:text-gray-300 leading-relaxed text-justify ${
                          !isExpanded ? 'line-clamp-2' : ''
                        }`}>
                          {item.abstract_ar}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons Bar */}
                    <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                      {/* PDF Download */}
                      {pdfUrl && (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0F388E] hover:bg-[#1447b3] text-white text-xs sm:text-sm font-bold shadow-md hover:shadow-lg transition-all"
                        >
                          <Download className="w-4 h-4" />
                          <span>تحميل وقراءة البحث (PDF)</span>
                        </a>
                      )}

                      {/* DOI / External Link */}
                      {item.research_url && (
                        <a
                          href={item.research_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#152843] text-gray-800 dark:text-gray-200 text-xs sm:text-sm font-bold hover:border-[#0F388E] dark:hover:border-[#64B5F6] hover:text-[#0F388E] shadow-sm transition-all"
                        >
                          <Globe className="w-4 h-4 text-[#C9A040]" />
                          <span>{item.doi ? 'رابط البحث (DOI)' : 'رابط النشر في المجلة'}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                        </a>
                      )}

                      {/* Center Link */}
                      <Link
                        to="/departments/research-center"
                        className="mr-auto inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-[#0F388E] dark:hover:text-[#64B5F6] transition-colors"
                      >
                        <span>مركز البحث العلمي</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12 flex-wrap">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(prev => Math.max(1, prev - 1));
                window.scrollTo({ top: 400, behavior: 'smooth' });
              }}
              className="text-xs sm:text-sm font-bold border-gray-300 dark:border-gray-700"
            >
              السابق
            </Button>

            <div className="flex items-center gap-1.5 flex-wrap">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2;
                })
                .map((page, idx, arr) => {
                  const prevPage = arr[idx - 1];
                  const showEllipsis = prevPage && page - prevPage > 1;

                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && <span className="px-2 text-gray-400 font-bold">...</span>}
                      <button
                        onClick={() => {
                          setCurrentPage(page);
                          window.scrollTo({ top: 400, behavior: 'smooth' });
                        }}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl text-xs sm:text-sm font-black transition-all ${
                          currentPage === page
                            ? 'bg-[#0F388E] text-white shadow-md'
                            : 'bg-white dark:bg-[#0B1A2F] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>

            <Button
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentPage(prev => Math.min(totalPages, prev + 1));
                window.scrollTo({ top: 400, behavior: 'smooth' });
              }}
              className="text-xs sm:text-sm font-bold border-gray-300 dark:border-gray-700"
            >
              التالي
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishedResearch;
